import { useOutletContext } from "react-router-dom";

import { HistoryMode, WorkspaceDataplane } from "src/clients/flow-api";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";

export const isIcebergEnabledForWorkspace = (
  workspace: Pick<WorkspaceDataplane, "config"> | undefined,
): boolean => {
  const featureFlagEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.readDHv2);
  const historyMode = workspace?.config?.history_mode;
  const readsFromIceberg =
    historyMode === HistoryMode.ICEBERG_READ_ICEBERG ||
    historyMode === HistoryMode.BOTH_READ_ICEBERG;

  return featureFlagEnabled || readsFromIceberg;
};

/**
 * Hook to determine if Iceberg (Decision History v2) should be used
 *
 * Returns true if either:
 * - The readDHv2 feature flag is enabled, OR
 * - The workspace history_mode reads from Iceberg (ICEBERG_READ_ICEBERG or BOTH_READ_ICEBERG)
 *
 * Use this hook in any component that needs to read from Decision History v2
 * (e.g., performance dashboard, decision history views, analytics)
 */
export const useIsIcebergEnabled = (): boolean => {
  const { workspace } = useWorkspaceContext();
  return isIcebergEnabledForWorkspace(workspace);
};

/**
 * Safe version of useIsIcebergEnabled that can be used outside workspace context.
 * Returns false when workspace context is not available.
 */
export const useIsIcebergEnabledSafe = (): boolean => {
  const context = useOutletContext<{ workspace?: WorkspaceDataplane }>();
  return isIcebergEnabledForWorkspace(context?.workspace);
};
